import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageProps } from '../types/auth';
import styles from './styles/Report.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Group {
  group_id: number;
  name: string;
  curriculum_id?: number;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Level {
  level_id: number;
  name: string;
}

const GroupDetailsReport: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedCurriculum, setSelectedCurriculum] = useState<string>('');
  const [selectedLevelFrom, setSelectedLevelFrom] = useState<string>('');
  const [selectedLevelTo, setSelectedLevelTo] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [levels, setLevels] = useState<Level[]>([]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Org-ID': selectedOrgID?.toString() || '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch groups');
        }

        const data: Group[] = await response.json();
        setGroups(data);
      } catch (err) {
        setError('Failed to load groups');
      }
    };

    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Org-ID': selectedOrgID?.toString() || '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch curriculums');
        }

        const data: Curriculum[] = await response.json();
        setCurriculums(data);
      } catch (err) {
        setError('Failed to load curriculums');
      }
    };

    fetchGroups();
    fetchCurriculums();
  }, [token, selectedOrgID]);

  useEffect(() => {
    if (selectedGroup) {
      const group = groups.find(g => g.group_id.toString() === selectedGroup);
      if (group?.curriculum_id) {
        setSelectedCurriculum(group.curriculum_id.toString());
      }
    }
  }, [selectedGroup, groups]);

  useEffect(() => {
    if (selectedCurriculum) {
      const fetchLevels = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/curriculums/${selectedCurriculum}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Org-ID': selectedOrgID?.toString() || '',
            },
          });

          if (!response.ok) {
            throw new Error('Failed to fetch levels');
          }

          const data = await response.json();
          setLevels(data.levels_sequence || []);
          // Reset level selections when curriculum changes
          setSelectedLevelFrom('');
          setSelectedLevelTo('');
        } catch (err) {
          setError('Failed to load levels');
          setLevels([]);
        }
      };

      fetchLevels();
    } else {
      setLevels([]);
    }
  }, [selectedCurriculum, token, selectedOrgID]);

  const handleSubmit = async () => {
    if (!selectedGroup || !selectedCurriculum || !selectedLevelFrom || !selectedLevelTo) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/reports/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Org-ID': selectedOrgID?.toString() || '',
        },
        body: JSON.stringify({
          report_name: 'group_details',
          parameters: {
            group_id: parseInt(selectedGroup, 10),
            curriculum_id: parseInt(selectedCurriculum, 10),
            level_from_id: parseInt(selectedLevelFrom, 10),
            level_to_id: parseInt(selectedLevelTo, 10)
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start report');
      }

      navigate('/reports');
    } catch (err) {
      setError('Failed to start report');
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Group Details Report</h1>
      
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.formGroup}>
        <label className={styles.label}>Group:</label>
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a group</option>
          {groups.map((group) => (
            <option key={group.group_id} value={group.group_id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Curriculum:</label>
        <select
          value={selectedCurriculum}
          onChange={(e) => setSelectedCurriculum(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a curriculum</option>
          {curriculums.map((curriculum) => (
            <option key={curriculum.curriculum_id} value={curriculum.curriculum_id}>
              {curriculum.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Level From:</label>
        <select
          value={selectedLevelFrom}
          onChange={(e) => setSelectedLevelFrom(e.target.value)}
          className={styles.select}
          disabled={!selectedCurriculum}
        >
          <option value="">Select starting level</option>
          {levels.map((level) => (
            <option key={level.level_id} value={level.level_id}>
              {level.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Level To:</label>
        <select
          value={selectedLevelTo}
          onChange={(e) => setSelectedLevelTo(e.target.value)}
          className={styles.select}
          disabled={!selectedCurriculum}
        >
          <option value="">Select ending level</option>
          {levels.map((level) => (
            <option key={level.level_id} value={level.level_id}>
              {level.name}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={handleSubmit}
        className={styles.submitButton}
        disabled={!selectedGroup || !selectedCurriculum || !selectedLevelFrom || !selectedLevelTo}
      >
        Run Report
      </button>
    </div>
  );
};

export default GroupDetailsReport; 